/** @jsxImportSource @emotion/react */

import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js/pure';
import {
  Elements,
  ExpressCheckoutElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import type {
  StripeElementsOptions,
  StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import { IBasePart } from '../part';
import useStripeAttemptPayment from '../../../hooks/actions/useStripeAttemptPayment';
import useSnowplowTracker from '../../../hooks/useSnowplowTracker';
import { logInfo, logWarning } from '../../../utils/remoteLogger';

export interface IStripeExpressCheckoutPart extends IBasePart {
  type: 'stripe_express_checkout';

  attemptUrl: string;
  publishableKey: string;
  centesimalValue: number;
  currency: string;
  relayPayload: object;
  buttonHeight: number;
  leadingMargin: number;
  trailingMargin: number;
  cornerRadius: number;
}

interface IExpressCheckoutContentProps {
  attemptUrl: string;
  relayPayload: object;
  buttonHeight: number;
}

// Wallets ride Stripe's "card" payment_method_types rail - ExpressCheckoutElement
// decides client-side, from browser support, which wallet button(s) to actually show.
const ExpressCheckoutContent = ({
  attemptUrl,
  relayPayload,
  buttonHeight,
}: IExpressCheckoutContentProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const attemptPayment = useStripeAttemptPayment();
  const snowplowTracker = useSnowplowTracker();
  // undefined while Stripe resolves wallet eligibility, false once it has resolved to
  // no wallets - the two states render differently, see below.
  const [hasWallet, setHasWallet] = useState<boolean | undefined>(undefined);

  const handleConfirm = async (
    event: StripeExpressCheckoutElementConfirmEvent
  ) => {
    if (!stripe || !elements) {
      logWarning(
        'expressCheckout',
        'Express checkout confirmed before Stripe/Elements were ready'
      );
      event.paymentFailed();
      return;
    }

    const { error: submitError } = await elements.submit();

    if (submitError) {
      logWarning(
        'expressCheckout',
        'Failed to submit express checkout element',
        { message: submitError.message ?? '' }
      );
      event.paymentFailed();
      return;
    }

    const { error: paymentMethodError, paymentMethod } =
      await stripe.createPaymentMethod({ elements });

    if (paymentMethodError || !paymentMethod) {
      logWarning(
        'expressCheckout',
        'Failed to create payment method from express checkout',
        { message: paymentMethodError?.message ?? '' }
      );
      event.paymentFailed();
      return;
    }

    attemptPayment(stripe, {
      attemptUrl,
      relayPayload,
      paymentMethodId: paymentMethod.id,
      shouldSaveCard: false,
    }).catch((error) => {
      logWarning(
        'expressCheckout',
        'Express checkout payment attempt failed',
        error instanceof Error ? error : { message: String(error) }
      );
      event.paymentFailed();
    });
  };

  const handleClick = (event: StripeExpressCheckoutElementClickEvent) => {
    event.resolve();

    if (event.expressPaymentType === 'apple_pay') {
      snowplowTracker.trackEvent(
        snowplowTracker.event.APPLE_PAY_BUTTON_CLICKED
      );
    } else if (event.expressPaymentType === 'google_pay') {
      snowplowTracker.trackEvent(
        snowplowTracker.event.GOOGLE_PAY_BUTTON_CLICKED
      );
    }
  };

  const handleAvailablePaymentMethodsChange = (
    event: StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent
  ) => {
    const { paymentMethods } = event;
    const applePay = Boolean(paymentMethods?.applePay?.available);
    const googlePay = Boolean(paymentMethods?.googlePay?.available);

    // Stripe fires this on initial render as well as on later changes, so a missing
    // log means eligibility never resolved at all - a different failure from resolving
    // to no wallets, and otherwise indistinguishable since both just render nothing.
    logInfo('expressCheckout', 'Wallet availability resolved', {
      applePay: String(applePay),
      googlePay: String(googlePay),
    });

    setHasWallet(applePay || googlePay);
  };

  // Keep the element mounted while eligibility is pending - unmounting it aborts
  // Stripe's own availability checks - and only collapse the space it reserves once
  // we know no button will render.
  //
  // Stripe's default ("auto") behavior decides whether to show each wallet based on its
  // own conversion-optimization heuristic, not purely technical eligibility - it can hide
  // a wallet even when the browser/account genuinely support it. "always" forces it to
  // show whenever the platform supports it, prompting a sign-in flow if needed.
  // buttonType text is rendered and localized by Google/Apple themselves (based on the
  // browser's own locale), not by us - we can only pick from their predefined label sets.
  // "pay" gives Google's "Pay with G Pay" wording; Apple has no direct equivalent, so
  // "plain" (logo only, no wording) avoids a similarly mismatched label there.
  const pendingWallet = hasWallet === undefined;
  const resolvedWithoutWallet = hasWallet === false;

  return (
    <div
      css={{
        visibility: hasWallet ? 'visible' : 'hidden',
        // Wallet eligibility takes a moment to resolve, so reserve the button's space
        // up front - otherwise everything below it shifts down when the button appears.
        // Browsers that resolve to no wallet collapse the reserved space again.
        minHeight: pendingWallet ? buttonHeight : undefined,
        height: resolvedWithoutWallet ? 0 : undefined,
        overflow: resolvedWithoutWallet ? 'hidden' : undefined,
      }}
    >
      <ExpressCheckoutElement
        options={{
          paymentMethods: {
            googlePay: 'always',
            applePay: 'always',
          },
          buttonType: { googlePay: 'pay', applePay: 'plain' },
          buttonHeight,
          layout: { maxColumns: 1 },
        }}
        onConfirm={handleConfirm}
        onClick={handleClick}
        onAvailablePaymentMethodsChange={handleAvailablePaymentMethodsChange}
      />
    </div>
  );
};

const StripeExpressCheckoutPart = (data: IStripeExpressCheckoutPart) => {
  const [stripePromise] = useState(() => loadStripe(data.publishableKey));

  // Without a real PaymentIntent clientSecret (we use the deferred mode/amount/currency
  // pattern), Stripe has no server-side record of which payment methods are enabled for
  // this Element group, so it needs paymentMethodTypes explicitly to run wallet eligibility
  // checks at all. The existing card sheet (stripeCheckoutOverlay.tsx) always passes
  // ["card"] for the same reason - wallets ride the card rail, so this is enough for
  // ExpressCheckoutElement to detect Apple Pay / Google Pay itself.
  //
  // ExpressCheckoutElement has no per-element corner radius option - it's controlled via
  // the Elements group's appearance API instead.
  const options: StripeElementsOptions = {
    mode: 'payment',
    currency: data.currency.toLowerCase(),
    amount: data.centesimalValue,
    paymentMethodCreation: 'manual',
    paymentMethodTypes: ['card'],
    appearance: {
      variables: { borderRadius: `${data.cornerRadius}px` },
    },
  };

  return (
    <div
      css={{
        marginLeft: data.leadingMargin,
        marginRight: data.trailingMargin,
      }}
    >
      <Elements stripe={stripePromise} options={options}>
        <ExpressCheckoutContent
          attemptUrl={data.attemptUrl}
          relayPayload={data.relayPayload}
          buttonHeight={data.buttonHeight}
        />
      </Elements>
    </div>
  );
};

export default StripeExpressCheckoutPart;
